import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { AlertTriangle, Check, ArrowRight, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export interface ProfileConflict {
  field: string;
  fieldLabel: string;
  aiviaValue: string | null;
  worldviaValue: string | null;
  worldviaUpdatedAt?: string;
  aiviaUpdatedAt?: string;
}

interface ProfileConflictResolverProps {
  conflicts: ProfileConflict[];
  isOpen: boolean;
  onClose: () => void;
  onResolve: (field: string, choice: 'aivia' | 'worldvia') => Promise<void>;
  onResolveAll?: () => void;
}

export default function ProfileConflictResolver({
  conflicts,
  isOpen,
  onClose,
  onResolve,
  onResolveAll,
}: ProfileConflictResolverProps) {
  const { toast } = useToast();
  const [resolvingField, setResolvingField] = useState<string | null>(null);
  const reviewLaterRef = useRef<HTMLButtonElement>(null);
  const [resolvedFields, setResolvedFields] = useState<Set<string>>(new Set());

  const remainingConflicts = conflicts.filter(
    (c) => !resolvedFields.has(c.field)
  );

  const handleResolve = async (field: string, choice: 'aivia' | 'worldvia') => {
    setResolvingField(field);
    try {
      await onResolve(field, choice);
      setResolvedFields((prev) => new Set([...prev, field]));
      toast({
        title: 'Conflict resolved',
        description: `Updated ${conflicts.find((c) => c.field === field)?.fieldLabel || field}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resolve conflict. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setResolvingField(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const truncateValue = (value: string | null, maxLength = 100) => {
    if (!value) return '(empty)';
    if (value.length <= maxLength) return value;
    return value.substring(0, maxLength) + '...';
  };

  if (remainingConflicts.length === 0 && resolvedFields.size > 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              All Conflicts Resolved
            </DialogTitle>
            <DialogDescription>
              Your profile is now in sync with worldvia.pro.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {/* AR-210 (TC-19.2): Radix focused the first 'Use this' action on open, so a
          stray Enter overwrote a field. Start on the safe 'Review Later' instead. */}
      <DialogContent
        className="sm:max-w-2xl max-h-[80vh] overflow-y-auto"
        onOpenAutoFocus={(e) => {
          if (!reviewLaterRef.current) return;
          e.preventDefault();
          reviewLaterRef.current.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Profile Sync Conflicts
          </DialogTitle>
          <DialogDescription>
            Some of your profile data differs from worldvia.pro. Choose which
            value to keep for each field.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {remainingConflicts.map((conflict) => (
            <Card key={conflict.field} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-sm">{conflict.fieldLabel}</h4>
                  {resolvingField === conflict.field && (
                    <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* WorldVia.pro Value */}
                  <div
                    className="border rounded-lg p-3 cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors"
                    onClick={() => handleResolve(conflict.field, 'worldvia')}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">
                        worldvia.pro
                      </Badge>
                      {conflict.worldviaUpdatedAt && (
                        <span className="text-xs text-muted-foreground">
                          Updated {formatDate(conflict.worldviaUpdatedAt)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm break-words">
                      {truncateValue(conflict.worldviaValue)}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                      disabled={resolvingField === conflict.field}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResolve(conflict.field, 'worldvia');
                      }}
                    >
                      <ArrowRight className="h-3 w-3 mr-1" />
                      Use this
                    </Button>
                  </div>

                  {/* AIVIA Value */}
                  <div
                    className="border rounded-lg p-3 cursor-pointer hover:border-green-500 hover:bg-green-50/50 dark:hover:bg-green-950/20 transition-colors"
                    onClick={() => handleResolve(conflict.field, 'aivia')}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="text-xs">
                        AIVIA (yours)
                      </Badge>
                      {conflict.aiviaUpdatedAt && (
                        <span className="text-xs text-muted-foreground">
                          Edited {formatDate(conflict.aiviaUpdatedAt)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm break-words">
                      {truncateValue(conflict.aiviaValue)}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-green-600 hover:text-green-700 hover:bg-green-100"
                      disabled={resolvingField === conflict.field}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResolve(conflict.field, 'aivia');
                      }}
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Keep mine
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="text-sm text-muted-foreground">
            {remainingConflicts.length} conflict
            {remainingConflicts.length !== 1 ? 's' : ''} remaining
          </div>
          <div className="flex gap-2">
            <Button ref={reviewLaterRef} variant="outline" onClick={onClose}>
              Review Later
            </Button>
            {onResolveAll && remainingConflicts.length > 1 && (
              <Button variant="secondary" onClick={onResolveAll}>
                Keep All AIVIA Values
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
